import React from 'react';
import { extend, media, stylesheet } from 'typestyle';
import { GcpRequest } from '../../../../../types/gcpRequet.types';
import { SuggestedGcpType } from '../../../../../consts/suggestedGcpType';
import { GcpCorruptionType } from '../../../../../consts/gcpCorruptionType';
import { GcpInstructionType } from '../../../../../consts/gcpInstructionType';
import { GcpRequestButton } from './buttons/GcpRequestButton';
import { useAppStore } from '../../../../../store/appStore';
import { DownloadGcpRequestButton } from './buttons/DownloadGcpRequestButton';
import { DeterminedPlacementResponsibility } from '../../../../../consts/daterminedPlacementResponsibility';
import { BREAKPOINT_SMALL } from '../../../../layout/styles';

const css = stylesheet({
  container: extend(
    {
      flex: '0 0 230px',
      width: 230,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        flex: 'none',
      },
    ),
  ),
  row: {
    display: 'grid',
    marginBottom: 8,
    gridTemplateColumns: 'auto 1fr auto',
    columnGap: 2,
  },
  filler: {
    backgroundImage: 'linear-gradient(to right, black 1px, transparent 1px)',
    backgroundPosition: '0 85%',
    backgroundRepeat: 'repeat-x',
    backgroundSize: '3px 1px',
  },
});

const calculateRemeasureCount = (gcpRequest: GcpRequest) => {
  const remeasureKeys = Object.keys(gcpRequest.instructions);
  const corruptedKeys = gcpRequest.corrupted.map(({ flightMarkerId }) => flightMarkerId);

  return remeasureKeys.filter(
    (key) => !corruptedKeys.includes(key) && gcpRequest.instructions[key].includes(GcpInstructionType.REMEASURE),
  ).length;
};

interface Props {
  gcpRequest: GcpRequest;
}

export const MissionWindowGcpRequest = ({ gcpRequest }: Props) => {
  const { selectedMission } = useAppStore();

  if (!selectedMission) return;

  const { customer, site, gcpPlacementService } = selectedMission;

  const existingControlsCount =
    gcpPlacementService &&
    gcpPlacementService.determinedPlacementResponsibility !== DeterminedPlacementResponsibility.Client
      ? gcpRequest.existing.length
      : 0;

  const countMap = {
    'Existing Controls': existingControlsCount,
    'New Markers': gcpRequest.suggested.filter(({ type }) => type === SuggestedGcpType.ADD_NEW).length,
    'Send Coordinates': gcpRequest.suggested.filter(({ type }) => type === SuggestedGcpType.SEND_COORDINATES).length,
    Remeasure: calculateRemeasureCount(gcpRequest),
    Replace: gcpRequest.corrupted.filter(({ type }) =>
      [GcpCorruptionType.REPLACE_BROKEN, GcpCorruptionType.REPLACE_SMALL].includes(type),
    ).length,
    'Clean Up / Repaint': gcpRequest.corrupted.filter(({ type }) =>
      [GcpCorruptionType.CLEANUP_DIRT, GcpCorruptionType.CLEANUP_REPAINT, GcpCorruptionType.CLEANUP_STUFF].includes(
        type,
      ),
    ).length,
    'Check Condition': gcpRequest.corrupted.filter(({ type }) => type === GcpCorruptionType.CHECK_CONDITION).length,
  };

  const totalCount = Object.values(countMap).reduce((sum, count) => sum + count, 0);

  return (
    <div className={css.container}>
      <div>
        {Object.entries(countMap).map(
          ([key, count]) =>
            count > 0 && (
              <div className={css.row} key={key}>
                <div>{key}</div>
                <div className={css.filler} />
                <div>{count}</div>
              </div>
            ),
        )}

        <div className={css.row}>
          <div>Total</div>
          <div className={css.filler} />
          <div>{totalCount}</div>
        </div>
      </div>

      <GcpRequestButton customer={customer} site={site} />

      <DownloadGcpRequestButton customer={customer} site={site} />
    </div>
  );
};
